// Google Identity Services (GIS) client for the dashboard.
// Replaces the PIN gate when config.USE_GAS_AUTH = true.
//
// Flow:
//   1. Load the GIS script (https://accounts.google.com/gsi/client).
//   2. Render a Sign-In button. User picks a Google account.
//   3. GIS calls our callback with an ID token (JWT, signed by Google).
//   4. We POST the token to the GAS backend, which returns { role, data }.
//   5. Token + role stored in sessionStorage for the tab lifetime.
//
// Security notes:
//   - The token is short-lived (~1 hour) and signed by Google — we don't trust it
//     on the client; the GAS backend re-verifies via tokeninfo before every fetch.
//   - We pin the audience (OAUTH_CLIENT_ID) on the GAS side, so a token issued for
//     a different app cannot be reused here.

import { OAUTH_CLIENT_ID, HOSTED_DOMAIN, GAS_URL } from './config.js';

const SS_KEY = 'hd:v2:gsi_token';
const ROLE_KEY = 'hd:v2:gsi_role';
const EMAIL_KEY = 'hd:v2:gsi_email';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

let _gisReady = null;

function loadGis() {
  if (_gisReady) return _gisReady;
  _gisReady = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const s = document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return _gisReady;
}

export function getCachedSession() {
  try {
    return {
      token: sessionStorage.getItem(SS_KEY),
      role: sessionStorage.getItem(ROLE_KEY),
      email: sessionStorage.getItem(EMAIL_KEY),
    };
  } catch { return { token: null, role: null, email: null }; }
}

function setCachedSession({ token, role, email }) {
  try {
    if (token) sessionStorage.setItem(SS_KEY, token);
    if (role) sessionStorage.setItem(ROLE_KEY, role);
    if (email) sessionStorage.setItem(EMAIL_KEY, email);
  } catch {}
}

export function clearSession() {
  try {
    sessionStorage.removeItem(SS_KEY);
    sessionStorage.removeItem(ROLE_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
  } catch {}
}

/**
 * Render the Google sign-in button inside the supplied container element.
 * Resolves once the user has signed in successfully.
 *
 *   const { role, email, token } = await signIn(document.getElementById('pin-gate'));
 */
export async function signIn(container) {
  if (!OAUTH_CLIENT_ID) throw new Error('OAUTH_CLIENT_ID not configured — edit src/config.js');
  if (!GAS_URL) throw new Error('GAS_URL not configured — edit src/config.js');
  await loadGis();

  return new Promise((resolve, reject) => {
    window.google.accounts.id.initialize({
      client_id: OAUTH_CLIENT_ID,
      callback: async (response) => {
        try {
          const verified = await verifyWithBackend(response.credential, 'whoami');
          if (!verified.ok) throw new Error(verified.error || 'auth_failed');
          setCachedSession({
            token: response.credential,
            role: verified.role,
            email: verified.email,
          });
          resolve(verified);
        } catch (e) { reject(e); }
      },
      hosted_domain: HOSTED_DOMAIN || undefined,
      auto_select: false,
      cancel_on_tap_outside: false,
    });

    // If there's already a valid session, fast-path through.
    const cached = getCachedSession();
    if (cached.token && cached.role) {
      verifyWithBackend(cached.token, 'whoami')
        .then((r) => r.ok ? resolve(r) : renderButton(container, reject))
        .catch(() => renderButton(container, reject));
      return;
    }
    renderButton(container, reject);
  });
}

function renderButton(container, onErr) {
  if (!container) return onErr(new Error('signIn: no container element'));
  container.innerHTML = '';
  const btnWrap = document.createElement('div');
  btnWrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:18px';
  const headline = document.createElement('div');
  headline.style.cssText = 'font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:#fff';
  headline.textContent = 'HELIDIRECT';
  const sub = document.createElement('div');
  sub.style.cssText = 'font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:#555;margin-bottom:8px';
  sub.textContent = '2026 KPI Dashboard';
  btnWrap.appendChild(headline);
  btnWrap.appendChild(sub);
  const btnDiv = document.createElement('div');
  btnWrap.appendChild(btnDiv);
  container.appendChild(btnWrap);
  window.google.accounts.id.renderButton(btnDiv, {
    theme: 'filled_black',
    size: 'large',
    type: 'standard',
    text: 'signin_with',
    shape: 'rectangular',
  });
  // Also try One Tap for returning users.
  try { window.google.accounts.id.prompt(); } catch {}
}

/** POST { id_token, action } to the GAS backend; returns parsed JSON. */
export async function verifyWithBackend(idToken, action = 'whoami', extra = {}) {
  const res = await fetch(GAS_URL, {
    method: 'POST',
    // Apps Script /exec accepts text/plain (avoiding preflight). JSON.parse is server-side.
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ id_token: idToken, action, ...extra }),
  });
  if (!res.ok) throw new Error(`backend_http_${res.status}`);
  return res.json();
}

export function signOut() {
  clearSession();
  try { window.google?.accounts?.id?.disableAutoSelect(); } catch {}
  location.reload();
}
